import { useEffect, useRef, useState } from 'preact/hooks';
import { Icons } from '../../components/icons/icon-set';
import { Terminal, StatusBar } from './terminal-output';
import type { DisplayMode } from '../../app-types';
import type { ResultMap, RunningMap, RunDisplay } from './result-types';

/** True when stdout text differs across ≥2 versions. */
export function outputsDiffer(results: ResultMap): boolean {
  const keys = Object.keys(results);
  if (keys.length < 2) return false;
  const sig = (r?: RunDisplay) => (r ? r.lines.map((l) => l.t).join('\n') : '');
  const first = sig(results[keys[0]]);
  return keys.some((k) => sig(results[k]) !== first);
}

interface ResultsPanelProps {
  versions: string[];
  results: ResultMap;
  running: RunningMap;
  activeTab: string;
  setActiveTab: (v: string) => void;
  displayMode: DisplayMode;
}

/** Per-version results, switching between single/tabs/columns/stacked layouts.
 * Ported 1:1 from claude_design/components2.jsx (ResultsPanel). */
export function ResultsPanel({
  versions,
  results,
  running,
  activeTab,
  setActiveTab,
  displayMode,
}: ResultsPanelProps) {
  const differ = outputsDiffer(results);

  const bodyRef = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(700);
  useEffect(() => {
    if (!bodyRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((e) => setW(e[0].contentRect.width));
    ro.observe(bodyRef.current);
    return () => ro.disconnect();
  }, []);

  let mode: string = displayMode;
  if (mode === 'auto') {
    if (versions.length <= 1) mode = 'single';
    else if (w >= versions.length * 300) mode = 'columns';
    else mode = 'stacked';
  }
  if (versions.length <= 1 && (mode === 'columns' || mode === 'stacked')) mode = 'single';

  const VerLabel = ({ v }: { v: string }) => {
    const r = results[v];
    if (running[v]) {
      return (
        <>
          <span class="statusdot-spin" />
          <span class="ver">{v}</span>
        </>
      );
    }
    const cls = !r
      ? 'idle'
      : r.exitCode === 0
        ? r.lines.some((l) => l.c === 'warn' || l.c === 'deprecated')
          ? 'warn'
          : 'ok'
        : 'err';
    return (
      <>
        <span class={'statusdot ' + cls} />
        <span class="ver">{v}</span>
      </>
    );
  };

  if (mode === 'single') {
    const v = versions[0];
    return (
      <div class="pane-body" ref={bodyRef}>
        <Terminal result={results[v]} running={running[v]} />
        <StatusBar result={results[v]} version={v} running={running[v]} />
      </div>
    );
  }

  if (mode === 'tabs') {
    const act = activeTab && versions.includes(activeTab) ? activeTab : versions[0];
    return (
      <div class="pane-body" ref={bodyRef}>
        <div class="restabs">
          {versions.map((v) => (
            <button key={v} class="restab" data-on={v === act} onClick={() => setActiveTab(v)}>
              <VerLabel v={v} />
            </button>
          ))}
        </div>
        {differ && versions.length > 1 && (
          <div class="diff-banner">
            <Icons.diff size={15} /> Results differ across versions — check each tab.
          </div>
        )}
        {/* key by version so switching tabs re-mounts the terminal — resets the
            virtualized scroll position instead of inheriting the prior tab's. */}
        <Terminal key={act} result={results[act]} running={running[act]} />
        <StatusBar result={results[act]} version={act} running={running[act]} />
      </div>
    );
  }

  if (mode === 'columns') {
    return (
      <div class="pane-body" ref={bodyRef}>
        {differ && versions.length > 1 && (
          <div class="diff-banner">
            <Icons.diff size={15} /> Results differ across versions.
          </div>
        )}
        <div class="results-columns">
          {versions.map((v) => (
            <div class="rescol" key={v}>
              <div class="rescol-head">
                <VerLabel v={v} />{' '}
                <span
                  style={{
                    color: 'var(--text-faint)',
                    fontWeight: 400,
                    fontFamily: 'var(--font-ui)',
                    fontSize: 12,
                  }}
                >
                  {results[v] ? results[v]!.timeMs + ' ms' : ''}
                </span>
              </div>
              <Terminal result={results[v]} running={running[v]} />
              <StatusBar result={results[v]} version={v} running={running[v]} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div class="pane-body" ref={bodyRef}>
      {differ && versions.length > 1 && (
        <div class="diff-banner">
          <Icons.diff size={15} /> Results differ across versions.
        </div>
      )}
      <div class="results-stacked">
        {versions.map((v) => {
          const r = results[v];
          return (
            <div class="stack-card" key={v}>
              <div class="stack-card-head">
                <VerLabel v={v} />
                <span
                  style={{
                    marginLeft: 'auto',
                    fontFamily: 'var(--font-ui)',
                    fontWeight: 600,
                    fontSize: 12,
                    color: 'var(--text-faint)',
                    display: 'flex',
                    gap: 14,
                  }}
                >
                  {r && <span>{r.timeMs} ms</span>}
                  {r && <span>{r.memMB} MB</span>}
                  {r && <span class={r.exitCode === 0 ? 'exit-ok' : 'exit-err'}>exit {r.exitCode}</span>}
                </span>
              </div>
              <Terminal result={r} running={running[v]} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
